/** Editor body for a multilingual value — an object keyed by ISO language code
 *  (`{en: "...", es: "..."}`). One compact row per language; each leaf uses the
 *  shared TagValueInput so a multilingual field can also be multiline / template
 *  / HTML. Adding a language is behind a small "Add language" button that reveals
 *  a searchable picker. The collapsed summary is rendered by the caller
 *  (TagDataField). */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { COMMON_LANGUAGES, languageLabel } from '../schema/vocabulary';
import { TagValueInput, type ValueFlags } from './TagValueInput';
import { TranslationBindingChip } from './TranslationBindingChip';
import type { LeafBindings } from './translationBindings';

interface LangOption {
  code: string;
  label: string;
}

interface Props {
  value: unknown;
  onChange: (next: Record<string, string>) => void;
  readOnly?: boolean;
  leafFlags?: ValueFlags;
  /** Translation bindings per language leaf (metaplan A6): marks auto-translated leaves. */
  bindings?: Record<string, LeafBindings>;
}

export function asLangMap(value: unknown): Record<string, string> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = v == null ? '' : String(v);
    }
    return out;
  }
  return {};
}

export function MultilingualField({ value, onChange, readOnly = false, leafFlags, bindings }: Props) {
  const map = useMemo(() => asLangMap(value), [value]);
  const langs = Object.keys(map);
  const [adding, setAdding] = useState(false);

  const available: LangOption[] = COMMON_LANGUAGES.filter((l) => !(l.code in map)).map((l) => ({
    code: l.code,
    label: `${l.label} (${l.code})`,
  }));

  const setLeaf = (code: string, leaf: string) => onChange({ ...map, [code]: leaf });
  const removeLang = (code: string) => {
    const next = { ...map };
    delete next[code];
    onChange(next);
  };
  const add = (code: string) => {
    if (!code || code in map) return;
    onChange({ ...map, [code]: '' });
    setAdding(false);
  };

  return (
    <Box>
      <Stack spacing={0.75}>
        {langs.length === 0 && (
          <Typography variant="caption" color="text.secondary">No languages yet.</Typography>
        )}
        {langs.map((code) => (
          <Stack key={code} direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ width: bindings ? 112 : 72, pt: 1, flexShrink: 0 }}>
              <Typography variant="body2" title={languageLabel(code)}>{code}</Typography>
              <TranslationBindingChip leaf={bindings?.[code]} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TagValueInput
                value={map[code] ?? ''}
                readOnly={readOnly}
                flags={leafFlags}
                onChange={(v) => setLeaf(code, v)}
              />
            </Box>
            {!readOnly && (
              <IconButton size="small" color="error" onClick={() => removeLang(code)} aria-label={`Remove ${code}`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>

      {!readOnly && available.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {adding ? (
            <Autocomplete<LangOption>
              size="small"
              autoHighlight
              openOnFocus
              options={available}
              getOptionLabel={(o) => o.label}
              onChange={(_, v) => v && add(v.code)}
              sx={{ width: 240 }}
              renderInput={(params) => <TextField {...params} label="Add language" autoFocus placeholder="Search…" />}
            />
          ) : (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add language
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
